import Link from 'next/link';
import type { ReactNode } from 'react';

/** Shared building blocks for the v2 home page. */

export const WRAP = 'mx-auto w-full max-w-[1440px] px-4 md:px-10 xl:px-16';

type BtnKind = 'dark' | 'orange' | 'line' | 'white' | 'yellow';
const BTN: Record<BtnKind, string> = {
  dark: 'bg-hv-ink text-white hover:bg-black',
  orange: 'bg-hv-orange text-white hover:brightness-95',
  line: 'border-[1.5px] border-hv-ink text-hv-ink hover:bg-hv-ink hover:text-white',
  white: 'bg-white text-hv-ink hover:bg-hv-paper',
  yellow: 'bg-hv-yellow text-hv-ink hover:brightness-95',
};

export function Btn({ href, kind = 'dark', children, arrow, external, className = '' }: {
  href: string; kind?: BtnKind; children: ReactNode; arrow?: boolean; external?: boolean; className?: string;
}) {
  // min-h + wrapping: a long label set in the admin wraps inside the button instead of widening
  // the row past the screen edge.
  const cls = `inline-flex min-h-12 max-w-full items-center justify-center gap-2.5 rounded-xl px-6 py-2 text-center text-[15px] font-semibold transition ${BTN[kind]} ${className}`;
  const inner = (<>{children}{arrow && <ArrowIcon />}</>);
  if (external) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>;
  }
  return <Link href={href} className={cls}>{inner}</Link>;
}

export function ArrowIcon({ className = 'h-[18px] w-[18px]' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

export function Eyebrow({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`font-hv-mono text-[12px] uppercase tracking-[0.04em] text-hv-orange ${className}`}>{children}</div>;
}

/** Numbered section heading: number column on desktop, stacked on mobile. */
export function SectionHeading({ num, title, subtitle, right, tone = 'light' }: {
  num?: string; title: ReactNode; subtitle?: ReactNode; right?: ReactNode; tone?: 'light' | 'dark';
}) {
  const sub = tone === 'dark' ? 'text-white/70' : 'text-hv-soft';
  const numCls = tone === 'dark' ? 'text-hv-yellow' : 'text-hv-orange';
  return (
    <div className={`grid gap-3 md:items-end md:gap-6 ${num ? 'md:grid-cols-[90px_1fr_auto]' : 'md:grid-cols-[1fr_auto]'}`}>
      {num && <div className={`font-hv-mono text-[13px] md:pb-2 ${numCls}`}>{num}</div>}
      <div>
        <h2 className="text-[30px] leading-[1.05] tracking-[-0.02em] md:text-[44px]">{title}</h2>
        {subtitle && <p className={`mt-2.5 max-w-[620px] text-base ${sub}`}>{subtitle}</p>}
      </div>
      {right && <div className="md:justify-self-end">{right}</div>}
    </div>
  );
}

/** Content column aligned with section headings (offset by the number column on desktop). */
export function Indented({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`md:pl-[var(--hv-indent)] ${className}`}>{children}</div>;
}

/** Fill {placeholders} in a config string, e.g. fmt('{count} designs', { count: '1,255' }). */
export function fmt(template: string, vars: Record<string, string | number>) {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

export type Labels = import('@/lib/home-v2/schema').HomeSettings['labels'];

export function formatINR(n: number) {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

/** wa.me link with a prefilled message. */
export const waLink = (phone: string, text: string) => `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;

export function formatCount(n: number) {
  return n.toLocaleString('en-IN');
}
